import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { useConfirm } from "../context/ConfirmContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, CalendarCheck, Users, Trash2, ClipboardCheck, Search, CalendarClock, Hash, UserCheck, Pencil, Clock, BarChart3, FileDown, AlertTriangle, MessageSquare, HeartHandshake, X } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";
import { ukDate } from "../lib/dates";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";
const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const LEAD_OPTIONS = [{ v: 0, l: "Morning of the service" }, { v: 1, l: "1 hour before" }, { v: 2, l: "2 hours before" }, { v: 3, l: "3 hours before" }, { v: 6, l: "6 hours before" }, { v: 12, l: "12 hours before" }, { v: 24, l: "24 hours before" }, { v: 48, l: "2 days before" }];
const emptySchedule = { name: "", service_type: "Sunday Service", weekday: 6, start_time: "10:00", enabled: true, invite_lead_hours: 0 };

export default function Attendance() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [members, setMembers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({ title: "Sunday Celebration Service", service_date: new Date().toISOString().slice(0, 10), service_type: "Sunday Service", notes: "" });
  const [sheet, setSheet] = useState(null);
  const [attSet, setAttSet] = useState(new Set());
  const [q, setQ] = useState("");
  // headcount
  const [hcFor, setHcFor] = useState(null);
  const [hc, setHc] = useState({ total: 0, adult: 0, child: 0, visitor: 0, notes: "" });
  const [savingHc, setSavingHc] = useState(false);
  // schedules
  const [schedules, setSchedules] = useState([]);
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedEditing, setSchedEditing] = useState(null);
  const [schedForm, setSchedForm] = useState(emptySchedule);
  const [savingSched, setSavingSched] = useState(false);
  // absentees
  const [absentees, setAbsentees] = useState({ streak: 3, count: 0, members: [] });
  const [absQ, setAbsQ] = useState("");
  // report
  const [reportOpen, setReportOpen] = useState(false);
  const [reportPeriod, setReportPeriod] = useState("week");
  const [report, setReport] = useState(null);

  const load = async () => {
    const [s, m, sc, ab] = await Promise.all([api.get("/services"), api.get("/members"), api.get("/service-schedules"), api.get("/attendance/absentees")]);
    setServices(s.data); setMembers(m.data); setSchedules(sc.data); setAbsentees(ab.data);
  };
  useEffect(() => { load(); }, []);

  const loadReport = async (period) => {
    setReport(null);
    try { const { data } = await api.get(`/attendance/report`, { params: { period } }); setReport(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const openReport = () => { setReportOpen(true); loadReport(reportPeriod); };
  const changePeriod = (p) => { setReportPeriod(p); loadReport(p); };
  const downloadReportCsv = async () => {
    try {
      const res = await api.get(`/attendance/report.csv`, { params: { period: reportPeriod }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `attendance_${reportPeriod}.csv`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const messageAbsentees = () => {
    const ids = absentees.members.map((a) => a.member_id);
    if (!ids.length) return;
    navigate("/messaging", { state: { memberIds: ids } });
  };
  const sendToPastoral = async () => {
    const ids = absentees.members.filter((a) => a.care_stage !== "pastoral").map((a) => a.member_id);
    if (!ids.length) { toast.info("They're already in pastoral care"); return; }
    if (!(await confirm({ title: "Send to pastoral care?", description: `Move ${ids.length} absent member(s) into pastoral care for a check-in?`, confirmLabel: "Send to pastoral care" }))) return;
    try { await api.patch("/members/bulk/care-stage", { member_ids: ids, care_stage: "pastoral" }); toast.success(`${ids.length} member(s) sent to pastoral care`); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const dismissAbsentee = async (a) => {
    try { await api.post("/attendance/absentees/dismiss", { member_id: a.member_id }); setAbsentees((s) => ({ ...s, members: s.members.filter((x) => x.member_id !== a.member_id), count: s.count - 1 })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/services", form); toast.success("Service created"); setOpenNew(false); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openSheet = async (svc) => {
    const { data } = await api.get(`/services/${svc.id}`);
    setSheet(data);
    setAttSet(new Set(data.attendee_ids || []));
  };
  const toggle = (id) => { const n = new Set(attSet); n.has(id) ? n.delete(id) : n.add(id); setAttSet(n); };
  const saveSheet = async () => {
    await api.post(`/services/${sheet.id}/attendance`, { attendee_ids: [...attSet], visitor_count: sheet.headcount?.visitor || 0 });
    toast.success("Manual list saved");
    setSheet(null); load();
  };

  const openHeadcount = (svc) => {
    const h = svc.headcount || {};
    setHc({ total: h.total || 0, adult: h.adult || 0, child: h.child || 0, visitor: h.visitor || 0, notes: h.notes || "" });
    setHcFor(svc);
  };
  const saveHeadcount = async () => {
    setSavingHc(true);
    try {
      await api.post(`/services/${hcFor.id}/headcount`, {
        total: Number(hc.total) || 0, adult: Number(hc.adult) || 0,
        child: Number(hc.child) || 0, visitor: Number(hc.visitor) || 0, notes: hc.notes,
      });
      toast.success("Headcount recorded");
      setHcFor(null); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingHc(false); }
  };

  const del = (id) => {
    const s = services.find((x) => x.id === id);
    setServices((l) => l.filter((x) => x.id !== id));
    undoableDelete({ label: s?.title || "Service", entityType: "service", perform: () => api.delete(`/services/${id}`), onUndo: load });
  };

  // --- schedules ---
  const openNewSchedule = () => { setSchedEditing(null); setSchedForm(emptySchedule); };
  const openEditSchedule = (s) => { setSchedEditing(s.id); setSchedForm({ name: s.name, service_type: s.service_type, weekday: s.weekday, start_time: s.start_time, enabled: s.enabled, invite_lead_hours: s.invite_lead_hours || 0 }); };
  const saveSchedule = async () => {
    if (!schedForm.name.trim()) { toast.error("Give the service a name"); return; }
    setSavingSched(true);
    try {
      if (schedEditing) await api.put(`/service-schedules/${schedEditing}`, schedForm);
      else await api.post("/service-schedules", schedForm);
      toast.success(schedEditing ? "Schedule updated — upcoming services refreshed" : "Service added to the schedule");
      setSchedEditing(null); setSchedForm(emptySchedule); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingSched(false); }
  };
  const deleteSchedule = async (s) => {
    if (!(await confirm({ title: "Remove from schedule?", description: `Stop auto-generating "${s.name}"? Upcoming, untouched services for it will be removed. Past and recorded services stay.`, confirmLabel: "Remove" }))) return;
    try { await api.delete(`/service-schedules/${s.id}`); toast.success("Removed from schedule"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleSchedule = async (s) => {
    try { await api.put(`/service-schedules/${s.id}`, { ...s, enabled: !s.enabled }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const filtered = members.filter((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q.toLowerCase()));
  const attRegistered = filtered.filter((m) => attSet.has(m.id));
  const attNotRegistered = filtered.filter((m) => !attSet.has(m.id));
  const { page, setPage, pageCount, pageItems } = usePaged(services, 25);
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Attendance</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Services auto-generate from your schedule · members self-register · staff record a headcount</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="attendance-report-button" onClick={openReport}
            className="flex items-center gap-2 rounded-full px-4 py-2.5 font-medium border hover:bg-[#E3F1EC] transition-all" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
            <BarChart3 className="h-5 w-5" /> Report
          </button>
          <button data-testid="manage-schedule-button" onClick={() => setSchedOpen(true)}
            className="flex items-center gap-2 rounded-full px-4 py-2.5 font-medium border hover:bg-[#E3F1EC] transition-all" style={{ borderColor: "var(--sj-border)", color: "var(--sj-primary)" }}>
            <CalendarClock className="h-5 w-5" /> Service Schedule
          </button>
          <button data-testid="add-service-button" onClick={() => setOpenNew(true)}
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>
            <Plus className="h-5 w-5" /> One-off Service
          </button>
        </div>
      </div>

      {absentees.count > 0 && (
        <div className={`${CARD} p-5`} style={{ background: "#FFF7EC", borderColor: "#F5D9AE" }} data-testid="absentees-banner">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#B45309" }} />
              <div>
                <div className="font-semibold text-sm" style={{ color: "#8a5a1a" }}>{absentees.count} member{absentees.count > 1 ? "s" : ""} missed the last {absentees.streak} services</div>
                <div className="text-xs" style={{ color: "#8a5a1a" }}>Not self-registered or counted in a row — a good moment for a pastoral check-in.</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="absentees-message-btn" onClick={messageAbsentees} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold border hover:bg-white" style={{ borderColor: "#B45309", color: "#B45309" }}><MessageSquare className="h-4 w-4" /> Message all</button>
              <button data-testid="absentees-pastoral-btn" onClick={sendToPastoral} className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "#B45309" }}><HeartHandshake className="h-4 w-4" /> Send to pastoral care</button>
            </div>
          </div>
          <div className="mt-3">
            <div className="relative mb-2">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "#B45309" }} />
              <input data-testid="absentees-search" placeholder="Search absent members…" value={absQ} onChange={(e) => setAbsQ(e.target.value)}
                className="w-full bg-white border rounded-xl outline-none focus:ring-2 focus:ring-[#B45309]/20 px-4 py-2.5 text-sm pl-10" style={{ borderColor: "#F5D9AE", color: "#8a5a1a" }} />
            </div>
            {(() => {
              const shown = absentees.members.filter((a) => (a.name || "").toLowerCase().includes(absQ.toLowerCase()));
              return (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5 max-h-[260px] overflow-y-auto pr-1" data-testid="absentees-list">
                  {shown.length === 0 && <div className="col-span-full text-center text-sm py-4" style={{ color: "#8a5a1a" }}>No absent members match your search.</div>}
                  {shown.map((a) => (
                    <div key={a.member_id} data-testid={`absentee-${a.member_id}`} className="flex items-center gap-2 rounded-xl bg-white pl-3 pr-1.5 py-2 border" style={{ borderColor: "#F5D9AE" }}>
                      <span className="h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: "var(--sj-ochre)" }}>{(a.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                      <span className="flex-1 min-w-0 truncate text-sm font-medium" style={{ color: "#8a5a1a" }}>{a.name}</span>
                      {a.care_stage === "pastoral" && <span className="text-[9px] uppercase font-bold rounded px-1 py-0.5 shrink-0" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>In care</span>}
                      <button data-testid={`absentee-dismiss-${a.member_id}`} onClick={() => dismissAbsentee(a)} title="Dismiss — checked in with them" className="p-1 rounded-full hover:bg-[#FBEAD7] shrink-0"><X className="h-3.5 w-3.5" style={{ color: "#8a5a1a" }} /></button>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="services-grid">
        {pageItems.map((s) => {
          const isUpcoming = (s.service_date || "") > today;
          const hcTotal = s.headcount?.total;
          return (
          <div key={s.id} data-testid={`service-card-${s.id}`} className={`${CARD} p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(45,45,45,0.08)]`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#0B5E4E1A" }}>
                <CalendarCheck className="h-6 w-6" style={{ color: "#0B5E4E" }} />
              </div>
              <div className="flex items-center gap-1">
                {s.service_date === today && <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "#E3F1EC", color: "#0B5E4E" }}>Today</span>}
                {isUpcoming && <span className="text-[10px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5" style={{ background: "#EEF0F2", color: "var(--sj-text-3)" }}>Upcoming</span>}
                <button data-testid={`delete-service-${s.id}`} onClick={() => del(s.id)} className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <h3 className="mt-4 font-head text-lg font-semibold">{s.title}</h3>
            <p className="text-sm flex items-center gap-1.5" style={{ color: "var(--sj-text-2)" }}>
              {s.service_type} · {ukDate(s.service_date)}{s.start_time ? <><span>·</span><Clock className="h-3.5 w-3.5" />{s.start_time}</> : null}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <div className="rounded-xl p-3" style={{ background: "#F2F6F5" }} data-testid={`selfcert-${s.id}`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}><UserCheck className="h-3.5 w-3.5" /> Self-registered</div>
                <div className="font-head text-2xl font-extrabold mt-0.5">{s.checkin_count ?? 0}</div>
              </div>
              <div className="rounded-xl p-3" style={{ background: hcTotal != null ? "#E3F1EC" : "#F2F6F5" }} data-testid={`headcount-stat-${s.id}`}>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}><Hash className="h-3.5 w-3.5" /> Headcount</div>
                <div className="font-head text-2xl font-extrabold mt-0.5" style={{ color: hcTotal != null ? "#0B5E4E" : "var(--sj-text-3)" }}>{hcTotal != null ? hcTotal : "—"}</div>
              </div>
            </div>
            {s.headcount && (
              <p className="mt-2 text-xs" style={{ color: "var(--sj-text-3)" }} data-testid={`headcount-breakdown-${s.id}`}>
                {s.headcount.adult} adults · {s.headcount.child} children · {s.headcount.visitor} visitors · by {s.headcount.by_name}
              </p>
            )}
            <div className="mt-4 flex items-center gap-2">
              <button data-testid={`record-headcount-${s.id}`} onClick={() => openHeadcount(s)}
                className="flex-1 flex items-center justify-center gap-2 rounded-full py-2.5 font-medium text-white transition-all hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
                <Hash className="h-4 w-4" /> {s.headcount ? "Update headcount" : "Record headcount"}
              </button>
              <button data-testid={`take-attendance-${s.id}`} onClick={() => openSheet(s)} title="Mark a manual attendee list"
                className="flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-medium border transition-all hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-secondary)" }}>
                <ClipboardCheck className="h-4 w-4" /> List
              </button>
            </div>
          </div>
        ); })}
      </div>
      <Paginator page={page} pageCount={pageCount} onPage={setPage} testid="attendance-paginator" />

      {/* Create one-off service dialog */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">One-off Service</DialogTitle>
          <DialogDescription>Add a single extra service (e.g. a special programme). Recurring services come from the Service Schedule.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-4 mt-2">
            <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Title</label>
              <input data-testid="service-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Date</label>
                <input data-testid="service-date-input" required type="date" value={form.service_date} onChange={(e) => setForm({ ...form, service_date: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Type</label>
                <select data-testid="service-type-input" value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} className={inputCls}>
                  <option>Sunday Service</option><option>Midweek Service</option><option>Prayer Meeting</option><option>Special Program</option></select></div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpenNew(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="save-service-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Create</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Headcount dialog */}
      <Dialog open={!!hcFor} onOpenChange={(o) => !o && setHcFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Record headcount</DialogTitle>
          <DialogDescription>{hcFor?.title} · {ukDate(hcFor?.service_date)}{hcFor?.start_time ? ` · ${hcFor.start_time}` : ""}</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Total attendance</label>
              <input data-testid="hc-total-input" type="number" min={0} value={hc.total} onChange={(e) => setHc({ ...hc, total: e.target.value })} className={inputCls} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Adults</label>
                <input data-testid="hc-adult-input" type="number" min={0} value={hc.adult} onChange={(e) => setHc({ ...hc, adult: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Children</label>
                <input data-testid="hc-child-input" type="number" min={0} value={hc.child} onChange={(e) => setHc({ ...hc, child: e.target.value })} className={inputCls} /></div>
              <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Visitors</label>
                <input data-testid="hc-visitor-input" type="number" min={0} value={hc.visitor} onChange={(e) => setHc({ ...hc, visitor: e.target.value })} className={inputCls} /></div>
            </div>
            <div><label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Notes (optional)</label>
              <input data-testid="hc-notes-input" value={hc.notes} onChange={(e) => setHc({ ...hc, notes: e.target.value })} placeholder="e.g. counted by ushers at 10:30" className={inputCls} /></div>
            <DialogFooter>
              <button type="button" onClick={() => setHcFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button data-testid="save-headcount-button" disabled={savingHc} onClick={saveHeadcount} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingHc ? "Saving…" : "Save headcount"}</button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual attendee list dialog */}
      <Dialog open={!!sheet} onOpenChange={(o) => !o && setSheet(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">{sheet?.title}</DialogTitle>
            <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>{ukDate(sheet?.service_date)} · {attSet.size} on the list</p>
          </DialogHeader>
          <div className="relative my-2">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--sj-primary)" }} />
            <input data-testid="attendance-search" placeholder="Search members..." value={q} onChange={(e) => setQ(e.target.value)} className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm px-4 py-2.5 text-sm pl-10" />
          </div>
          <div className="flex-1 overflow-y-auto pr-1" style={{ maxHeight: 360 }}>
            <div className="flex items-center gap-2 sticky top-0 bg-white py-1.5 z-10">
              <UserCheck className="h-4 w-4" style={{ color: "#0B5E4E" }} />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "#0B5E4E" }}>Registered ({attRegistered.length})</span>
            </div>
            {attRegistered.length === 0 ? (
              <p className="text-xs px-2.5 py-2" style={{ color: "var(--sj-text-3)" }}>No one marked present yet — tick people below.</p>
            ) : attRegistered.map((m) => (
              <label key={m.id} data-testid={`att-member-${m.id}`} className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer hover:bg-[#EDF2F4]">
                <input type="checkbox" checked onChange={() => toggle(m.id)} className="h-4 w-4 accent-[#17A88A]" />
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: "var(--sj-ochre)" }}>{m.first_name[0]}{m.last_name[0]}</div>
                <span className="text-sm font-medium">{m.first_name} {m.last_name}</span>
              </label>
            ))}
            <div className="flex items-center gap-2 sticky top-0 bg-white py-1.5 mt-3 z-10 border-t" style={{ borderColor: "var(--sj-border)" }}>
              <Users className="h-4 w-4" style={{ color: "var(--sj-text-3)" }} />
              <span className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Not registered ({attNotRegistered.length})</span>
            </div>
            {attNotRegistered.length === 0 ? (
              <p className="text-xs px-2.5 py-2" style={{ color: "var(--sj-text-3)" }}>Everyone shown is marked present.</p>
            ) : attNotRegistered.map((m) => (
              <label key={m.id} data-testid={`att-member-${m.id}`} className="flex items-center gap-3 p-2.5 rounded-xl cursor-pointer hover:bg-[#EDF2F4]">
                <input type="checkbox" checked={false} onChange={() => toggle(m.id)} className="h-4 w-4 accent-[#17A88A]" />
                <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-semibold" style={{ background: "#B9C2C7" }}>{m.first_name[0]}{m.last_name[0]}</div>
                <span className="text-sm font-medium">{m.first_name} {m.last_name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setSheet(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
            <button data-testid="save-attendance-button" onClick={saveSheet} className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Save List</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Schedule dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-head text-2xl">Service Schedule</DialogTitle>
          <DialogDescription>Recurring services auto-generate for the next 4 weeks. Members get a self-registration invite on the morning of each service.</DialogDescription></DialogHeader>
          <div className="space-y-2 mt-2 max-h-[300px] overflow-y-auto pr-1" data-testid="schedule-list">
            {schedules.length === 0 && <p className="text-sm text-center py-4" style={{ color: "var(--sj-text-3)" }}>No recurring services yet — add one below.</p>}
            {schedules.map((s) => (
              <div key={s.id} data-testid={`schedule-row-${s.id}`} className="flex items-center justify-between rounded-xl border p-3" style={{ borderColor: "var(--sj-border)", opacity: s.enabled ? 1 : 0.6 }}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{s.name}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{s.weekday_name}s · {s.time_label} · {s.service_type}</div>
                  <div className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: "var(--sj-primary)" }} data-testid={`schedule-lead-${s.id}`}><CalendarClock className="h-3 w-3" /> Invite: {s.lead_label}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button data-testid={`schedule-toggle-${s.id}`} onClick={() => toggleSchedule(s)} className="text-xs font-semibold px-2.5 py-1 rounded-full border" style={{ borderColor: "var(--sj-border)", color: s.enabled ? "#0B5E4E" : "var(--sj-text-3)" }}>{s.enabled ? "On" : "Off"}</button>
                  <button data-testid={`schedule-edit-${s.id}`} onClick={() => openEditSchedule(s)} className="p-1.5 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-secondary)" }}><Pencil className="h-4 w-4" /></button>
                  <button data-testid={`schedule-delete-${s.id}`} onClick={() => deleteSchedule(s)} className="p-1.5 rounded-lg hover:bg-red-50" style={{ color: "#C0392B" }}><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--sj-border)" }} data-testid="schedule-form">
            <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>{schedEditing ? "Edit service" : "Add a recurring service"}</div>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="sched-name-input" placeholder="Name (e.g. Sunday First Service)" value={schedForm.name} onChange={(e) => setSchedForm({ ...schedForm, name: e.target.value })} className={inputCls} />
              <select data-testid="sched-type-input" value={schedForm.service_type} onChange={(e) => setSchedForm({ ...schedForm, service_type: e.target.value })} className={inputCls}>
                <option>Sunday Service</option><option>Midweek Service</option><option>Prayer Meeting</option><option>Special Program</option>
              </select>
              <select data-testid="sched-weekday-input" value={schedForm.weekday} onChange={(e) => setSchedForm({ ...schedForm, weekday: Number(e.target.value) })} className={inputCls}>
                {WEEKDAYS.map((w, i) => <option key={i} value={i}>{w}</option>)}
              </select>
              <input data-testid="sched-time-input" type="time" value={schedForm.start_time} onChange={(e) => setSchedForm({ ...schedForm, start_time: e.target.value })} className={inputCls} />
            </div>
            <div className="mt-2">
              <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Send the attendance invite</label>
              <select data-testid="sched-lead-input" value={schedForm.invite_lead_hours} onChange={(e) => setSchedForm({ ...schedForm, invite_lead_hours: Number(e.target.value) })} className={inputCls}>
                {LEAD_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              {schedEditing && <button type="button" onClick={openNewSchedule} className="rounded-full px-4 py-2 border font-medium text-sm" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel edit</button>}
              <button data-testid="save-schedule-button" disabled={savingSched} onClick={saveSchedule} className="rounded-full px-5 py-2 text-white font-semibold text-sm hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>{savingSched ? "Saving…" : schedEditing ? "Save changes" : "Add service"}</button>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setSchedOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Done</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attendance report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle className="font-head text-2xl">Attendance Report</DialogTitle>
          <DialogDescription>Self-registered check-ins vs recorded headcount across your services.</DialogDescription></DialogHeader>
          <div className="flex items-center justify-between gap-2 mt-2">
            <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "var(--sj-border)" }} data-testid="report-period-toggle">
              {[{ k: "week", l: "This week" }, { k: "month", l: "This month" }].map((p) => (
                <button key={p.k} data-testid={`report-period-${p.k}`} onClick={() => changePeriod(p.k)} className="rounded-full px-4 py-1.5 text-sm font-semibold transition-colors" style={reportPeriod === p.k ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)" }}>{p.l}</button>
              ))}
            </div>
            <button data-testid="report-csv-btn" onClick={downloadReportCsv} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[#E3F1EC]" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}><FileDown className="h-4 w-4" /> CSV</button>
          </div>
          {!report ? (
            <div className="py-10 text-center text-sm" style={{ color: "var(--sj-text-3)" }}>Loading report…</div>
          ) : (
            <div className="mt-3">
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="rounded-xl p-3" style={{ background: "#F2F6F5" }}><div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>Services</div><div className="font-head text-2xl font-extrabold">{report.totals.services}</div></div>
                <div className="rounded-xl p-3" style={{ background: "#F2F6F5" }}><div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>Self-registered</div><div className="font-head text-2xl font-extrabold" data-testid="report-total-selfreg">{report.totals.self_registered}</div></div>
                <div className="rounded-xl p-3" style={{ background: "#E3F1EC" }}><div className="text-[11px] uppercase tracking-wide" style={{ color: "var(--sj-text-3)" }}>Headcount</div><div className="font-head text-2xl font-extrabold" style={{ color: "#0B5E4E" }} data-testid="report-total-headcount">{report.totals.headcount_total}</div></div>
              </div>
              <div className="max-h-[320px] overflow-y-auto">
                <table className="min-w-full text-sm" data-testid="report-table">
                  <thead><tr style={{ color: "var(--sj-text-3)" }} className="text-left">
                    <th className="py-2 px-2">Date</th><th className="py-2 px-2">Service</th>
                    <th className="py-2 px-2 text-center">Self-reg</th><th className="py-2 px-2 text-center">Headcount</th>
                    <th className="py-2 px-2 text-center">A/C/V</th>
                  </tr></thead>
                  <tbody>
                    {report.rows.length === 0 ? (
                      <tr><td colSpan={5} className="py-8 text-center" style={{ color: "var(--sj-text-3)" }}>No services in this period yet.</td></tr>
                    ) : report.rows.map((r) => (
                      <tr key={r.id} data-testid={`report-row-${r.id}`} className="border-t" style={{ borderColor: "var(--sj-border)" }}>
                        <td className="py-2 px-2 whitespace-nowrap">{ukDate(r.date)}</td>
                        <td className="py-2 px-2">{r.title}<span className="block text-[11px]" style={{ color: "var(--sj-text-3)" }}>{r.start_time}</span></td>
                        <td className="py-2 px-2 text-center font-semibold">{r.self_registered}</td>
                        <td className="py-2 px-2 text-center font-semibold" style={{ color: r.has_headcount ? "#0B5E4E" : "var(--sj-text-3)" }}>{r.has_headcount ? r.headcount_total : "—"}</td>
                        <td className="py-2 px-2 text-center text-xs" style={{ color: "var(--sj-text-3)" }}>{r.has_headcount ? `${r.adult}/${r.child}/${r.visitor}` : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--sj-text-3)" }}>A/C/V = Adults / Children / Visitors. Avg headcount {report.totals.avg_headcount} · avg self-registered {report.totals.avg_self_registered}.</p>
              {report.absentees && (
                <div className="mt-4 rounded-xl p-4" style={{ background: report.absentees.count > 0 ? "#FFF7EC" : "#F2F6F5" }} data-testid="report-absentees">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-4 w-4" style={{ color: report.absentees.count > 0 ? "#B45309" : "var(--sj-text-3)" }} />
                    <span className="text-sm font-semibold" style={{ color: report.absentees.count > 0 ? "#8a5a1a" : "var(--sj-text-2)" }}>Absent follow-up — missed last {report.absentees.streak} services ({report.absentees.count})</span>
                  </div>
                  {report.absentees.count === 0 ? (
                    <p className="text-xs" style={{ color: "var(--sj-text-3)" }}>No one currently flagged (or not enough service history yet). Included in the CSV export.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {report.absentees.members.map((a) => (
                        <span key={a.member_id} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium border" style={{ borderColor: "#F5D9AE", color: "#8a5a1a" }}>{a.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setReportOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
